import React, { useState, useEffect } from 'react';
import { Search, Plus, Edit, Trash2, Star, Tag, BookOpen, Code, Palette, TrendingUp, Users, Calculator, Camera, Heart, Globe } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { categoriasAPI } from '../../services/api';
import IconSelector from '../../components/IconSelector';

const GerenciarCategorias = () => {
  const [categorias, setCategorias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [selectedCategoria, setSelectedCategoria] = useState(null);
  const [formData, setFormData] = useState({
    nome: '',
    descricao: '',
    icone: 'BookOpen'
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    carregarCategorias();
  }, []);

  const carregarCategorias = async () => {
    try {
      setLoading(true);
      const response = await categoriasAPI.listar();
      setCategorias(response.data.categorias || []);
    } catch (error) {
      console.error('Erro ao carregar categorias:', error);
      alert('Erro ao carregar categorias');
    } finally {
      setLoading(false);
    }
  };

  const filteredCategorias = categorias.filter(categoria =>
    categoria.nome?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    categoria.descricao?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const abrirModal = (mode, categoria = null) => {
    setModalMode(mode);
    setSelectedCategoria(categoria);
    
    if (mode === 'edit' && categoria) {
      setFormData({
        nome: categoria.nome || '',
        descricao: categoria.descricao || '',
        icone: categoria.icone || 'BookOpen'
      });
    } else {
      setFormData({
        nome: '',
        descricao: '',
        icone: 'BookOpen'
      });
    }
    
    setShowModal(true);
  };

  const fecharModal = () => {
    setShowModal(false);
    setSelectedCategoria(null);
    setFormData({
      nome: '',
      descricao: '',
      icone: 'BookOpen'
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.nome.trim()) {
      alert('Nome é obrigatório');
      return;
    }

    setSubmitting(true);
    
    try {
      if (modalMode === 'create') {
        await categoriasAPI.criar(formData);
        alert('Categoria criada com sucesso!');
      } else {
        await categoriasAPI.atualizar(selectedCategoria.id, formData);
        alert('Categoria atualizada com sucesso!');
      }
      
      fecharModal();
      carregarCategorias();
    } catch (error) {
      console.error('Erro ao salvar categoria:', error);
      const errorMsg = error.response?.data?.erro || 'Erro ao salvar categoria';
      alert(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (categoriaId, categoriaNome) => {
    if (!window.confirm(`Tem certeza que deseja deletar a categoria "${categoriaNome}"?`)) {
      return;
    }

    try {
      await categoriasAPI.deletar(categoriaId);
      alert('Categoria deletada com sucesso!');
      carregarCategorias();
    } catch (error) {
      console.error('Erro ao deletar categoria:', error);
      const errorMsg = error.response?.data?.erro || 'Erro ao deletar categoria';
      alert(errorMsg);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white">Gerenciar Categorias</h1>
          <p className="text-slate-400">Gerencie as categorias dos cursos</p>
        </div>
        <Button
          onClick={() => abrirModal('create')}
          className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nova Categoria
        </Button>
      </div>

      <Card className="bg-slate-800/50 border-slate-700">
        <CardContent className="p-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Buscar categorias por nome ou descrição..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </CardContent>
      </Card>

      {filteredCategorias.length === 0 ? (
        <Card className="p-12 bg-slate-800/50 border-slate-700/50 text-center">
          <Tag className="w-16 h-16 text-slate-400 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-white mb-2">
            {searchTerm ? 'Nenhuma categoria encontrada' : 'Nenhuma categoria cadastrada'}
          </h3>
          <p className="text-slate-400 mb-6">
            {searchTerm ? 'Tente alterar os termos de busca' : 'Crie sua primeira categoria para começar'}
          </p>
          {!searchTerm && (
            <Button 
              onClick={() => abrirModal('create')}
              className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nova Categoria
            </Button>
          )}
        </Card>
      ) : (
        <div className="grid gap-6">
          {filteredCategorias.map((categoria) => (
            <Card key={categoria.id} className="bg-slate-800/50 border-slate-700 hover:border-slate-600 transition-colors">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="p-2 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg">
                        {categoria.icone ? (() => {
                          const iconMap = { BookOpen, Code, Palette, TrendingUp, Users, Calculator, Camera, Heart, Globe, Star };
                          const IconComponent = iconMap[categoria.icone] || Star;
                          return <IconComponent className="w-5 h-5 text-white" />;
                        })() : <Star className="w-5 h-5 text-white" />}
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-white">{categoria.nome}</h3>
                        <Badge variant="secondary" className="bg-slate-700 text-slate-300">
                          ID: {categoria.id}
                        </Badge>
                      </div>
                    </div>
                    
                    {categoria.descricao && (
                      <p className="text-slate-300 leading-relaxed mb-4">
                        {categoria.descricao}
                      </p>
                    )}
                  </div>
                  
                  <div className="flex gap-2 ml-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => abrirModal('edit', categoria)}
                      className="border-blue-600 text-blue-400 hover:bg-blue-600 hover:text-white"
                    >
                      <Edit className="w-4 h-4 mr-1" />
                      Editar
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDelete(categoria.id, categoria.nome)}
                      className="border-red-600 text-red-400 hover:bg-red-600 hover:text-white"
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      Excluir
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Modal para criar/editar categoria */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-lg w-full max-w-md border border-slate-700">
            <div className="p-6 border-b border-slate-700">
              <h2 className="text-xl font-bold text-white">
                {modalMode === 'create' ? 'Nova Categoria' : 'Editar Categoria'}
              </h2>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Nome da Categoria *
                  </label>
                  <input
                    type="text"
                    value={formData.nome}
                    onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Ex: Desenvolvimento Web"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Descrição
                  </label>
                  <textarea
                    value={formData.descricao}
                    onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                    rows="3"
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    placeholder="Descrição da categoria (opcional)"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Ícone da Categoria
                  </label>
                  <IconSelector
                    selectedIcon={formData.icone}
                    onIconSelect={(iconName) => setFormData({ ...formData, icone: iconName })}
                  />
                </div>
              </div>
              
              <div className="flex gap-3 mt-6">
                <Button
                  type="button"
                  variant="outline"
                  onClick={fecharModal}
                  className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-700"
                  disabled={submitting}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800"
                  disabled={submitting}
                >
                  {submitting ? 'Salvando...' : modalMode === 'create' ? 'Criar' : 'Salvar'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default GerenciarCategorias; 